import type { Point } from '../contract/types.js';
import type { PathSegment, CurveSegment } from './subpath.js';

/**
 * Canonical circle and arc recognition — PRD Finding 1 made concrete.
 *
 * A PDF has no circle or arc primitive. Its drawing operations are straight
 * lines, cubic Bezier curves and rectangles. A circle drawn in a PDF comes back
 * as exactly four cubic curves. So producing the CIRCLE and ARC entities the
 * specification asks for is only possible by recognising the standard
 * construction PDF generators emit.
 *
 * Two different jobs sit behind the word "supported", and separating them is
 * what resolves the undefined boundary in the scope:
 *
 *   1. Recognising the canonical construction — bounded, testable, and almost
 *      certainly what is intended. That is what this module does.
 *   2. Fitting arbitrary freeform curves to arcs — open-ended, and what the
 *      exclusion was written to avoid. Deliberately not done here; a freeform
 *      curve is recorded as a quality issue instead.
 */

/** The constant PDF and SVG generators use to approximate a quarter circle. */
export const KAPPA = (4 / 3) * (Math.SQRT2 - 1);

export interface CircleShape {
  type: 'CIRCLE';
  centre: Point;
  radius: number;
}

export interface ArcShape {
  type: 'ARC';
  centre: Point;
  radius: number;
  startAngle: number;
  endAngle: number;
}

export type ArcRecognition = CircleShape | ArcShape;

/** Relative tolerance on radius agreement across the candidate's on-curve points. */
const RADIUS_REL_TOLERANCE = 0.01;

/** Relative tolerance on each control point against its canonical position. */
const CONTROL_REL_TOLERANCE = 0.02;

const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

const toDegrees = (rad: number): number => {
  const d = (rad * 180) / Math.PI;
  const r = d % 360;
  return r < 0 ? r + 360 : r;
};

/**
 * Returns a CIRCLE or ARC when the sub-path is the canonical circular
 * construction within tolerance, and null otherwise. Null means "record this as
 * a quality issue" — never "approximate it".
 */
export function recogniseCanonicalArc(sub: PathSegment[]): ArcRecognition | null {
  const start = sub.find((s): s is Extract<PathSegment, { kind: 'move' }> => s.kind === 'move');
  if (!start) return null;

  // A canonical construction is curves only. Any straight segment means this is
  // a mixed path, not a circle.
  if (sub.some((s) => s.kind === 'line')) return null;

  const curves = sub.filter((s): s is CurveSegment => s.kind === 'curve');
  if (curves.length === 0 || curves.length > 4) return null;

  const onCurve: Point[] = [start.to, ...curves.map((c) => c.to)];
  const centre = estimateCentre(start.to, curves[0]!);
  if (!centre) return null;

  const radii = onCurve.map((q) => dist(centre, q));
  const radius = radii.reduce((a, b) => a + b, 0) / radii.length;
  if (!(radius > 0) || !Number.isFinite(radius)) return null;
  if (radii.some((r) => Math.abs(r - radius) > radius * RADIUS_REL_TOLERANCE)) return null;

  // Each segment must itself be a canonical arc segment: its control points sit
  // at the tangent offset (4/3)tan(sweep/4) * radius from the on-curve points.
  let cursor: Point = start.to;
  for (const c of curves) {
    if (!isCanonicalSegment(centre, radius, cursor, c)) return null;
    cursor = c.to;
  }

  const closed = dist(start.to, cursor) <= radius * 1e-6;
  if (closed && curves.length === 4) {
    return { type: 'CIRCLE', centre, radius };
  }

  /*
    Which of the two arcs between these points the file actually drew.

    `startAngle` is the first on-curve point and `endAngle` the last, and every
    consumer sweeps from start to end **in the direction of increasing angle**.
    That is a guess about the traversal, and a PDF is free to draw an arc either
    way round — a CAD exporter's choice, not a property of the shape.

    Drawn the other way, the same two endpoints describe the COMPLEMENT. Measured
    on a 130° arc over the top of a circle, with byte-identical ink both times:

        forward  (200°→330°)  sweep 130  -> the top     correct
        reversed (330°→200°)  sweep 230  -> the bottom  the trace lands where
                                                        there is no ink at all

    So the direction is read off the curve. The signed angular step between
    consecutive on-curve points sums to the true swept angle; if that is negative
    the file drew the arc backwards, and the start and end are exchanged so that
    "start to end, increasing" always names the arc that was drawn. It is the same
    exchange `toDrawingSpace` makes for an orientation-reversing transform, for the
    same reason: reversing the direction of travel reverses which end is the start.
  */
  const walked = [start.to, ...curves.map((c) => c.to)];
  let swept = 0;
  for (let i = 1; i < walked.length; i += 1) {
    const a = Math.atan2(walked[i - 1]!.y - centre.y, walked[i - 1]!.x - centre.x);
    const b = Math.atan2(walked[i]!.y - centre.y, walked[i]!.x - centre.x);
    // Each step of the canonical construction is at most a quarter turn, so the
    // shorter way round is always the way the curve went.
    let step = b - a;
    while (step > Math.PI) step -= 2 * Math.PI;
    while (step < -Math.PI) step += 2 * Math.PI;
    swept += step;
  }

  const a0 = toDegrees(Math.atan2(start.to.y - centre.y, start.to.x - centre.x));
  const a1 = toDegrees(Math.atan2(cursor.y - centre.y, cursor.x - centre.x));
  return {
    type: 'ARC',
    centre,
    radius,
    startAngle: swept < 0 ? a1 : a0,
    endAngle: swept < 0 ? a0 : a1,
  };
}

/**
 * The centre lies where the normals to the tangents at the segment's endpoints
 * meet. Control point c1 lies along the tangent at the start; c2 along the
 * tangent at the end.
 */
function estimateCentre(p0: Point, first: CurveSegment): Point | null {
  const t0 = { x: first.c1.x - p0.x, y: first.c1.y - p0.y };
  const t1 = { x: first.to.x - first.c2.x, y: first.to.y - first.c2.y };
  if (Math.hypot(t0.x, t0.y) < 1e-12 || Math.hypot(t1.x, t1.y) < 1e-12) return null;

  const n0 = { x: -t0.y, y: t0.x };
  const n1 = { x: -t1.y, y: t1.x };

  const det = n0.x * -n1.y - n0.y * -n1.x;
  if (Math.abs(det) < 1e-12) return null;

  const rx = first.to.x - p0.x;
  const ry = first.to.y - p0.y;
  const s = (rx * -n1.y - ry * -n1.x) / det;

  const centre = { x: p0.x + n0.x * s, y: p0.y + n0.y * s };
  if (!Number.isFinite(centre.x) || !Number.isFinite(centre.y)) return null;
  return centre;
}

/**
 * One Bezier segment, and the circular arc it is — or null when it is not one.
 *
 * `recogniseCanonicalArc` answers the question for a whole sub-path and refuses
 * the moment it sees a straight segment, which is correct for a circle and is
 * why a rounded rectangle produced nothing at all: four edges and four corner
 * arcs is a mixed path, so the whole thing was recorded as an unsupported curve.
 *
 * This answers it per segment, so a mixed path can become a polyline whose
 * corners carry a bulge. `sweep` is signed and normalised into (-pi, pi], which
 * is what a bulge needs: `tan(sweep / 4)`.
 */
export interface ArcSegmentShape {
  centre: Point;
  radius: number;
  /** Signed included angle in radians, in (-pi, pi]. */
  sweep: number;
}

export function recogniseArcSegment(from: Point, c: CurveSegment): ArcSegmentShape | null {
  const centre = estimateCentre(from, c);
  if (!centre) return null;

  const r0 = dist(centre, from);
  const r1 = dist(centre, c.to);
  if (!(r0 > 0) || !Number.isFinite(r0)) return null;
  // Both endpoints have to sit on one circle. A Bezier whose ends are different
  // distances from the estimated centre is not an arc, however tidy its control
  // points look.
  if (Math.abs(r1 - r0) > r0 * RADIUS_REL_TOLERANCE) return null;

  const radius = (r0 + r1) / 2;
  if (!isCanonicalSegment(centre, radius, from, c)) return null;

  const a0 = Math.atan2(from.y - centre.y, from.x - centre.x);
  const a1 = Math.atan2(c.to.y - centre.y, c.to.x - centre.x);
  let sweep = a1 - a0;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep <= -Math.PI) sweep += 2 * Math.PI;
  if (Math.abs(sweep) < 1e-9) return null;

  return { centre, radius, sweep };
}

/** The bulge a swept angle corresponds to: `tan(theta / 4)`, sign and all. */
export function bulgeOfSweep(sweep: number): number {
  return Math.tan(sweep / 4);
}

function isCanonicalSegment(
  centre: Point,
  radius: number,
  from: Point,
  c: CurveSegment,
): boolean {
  const a0 = Math.atan2(from.y - centre.y, from.x - centre.x);
  const a1 = Math.atan2(c.to.y - centre.y, c.to.x - centre.x);

  let sweep = a1 - a0;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep <= -Math.PI) sweep += 2 * Math.PI;
  if (Math.abs(sweep) < 1e-9) return false;

  const k = (4 / 3) * Math.tan(sweep / 4);
  const expectedC1 = {
    x: from.x - k * (from.y - centre.y),
    y: from.y + k * (from.x - centre.x),
  };
  const expectedC2 = {
    x: c.to.x + k * (c.to.y - centre.y),
    y: c.to.y - k * (c.to.x - centre.x),
  };

  const tol = radius * CONTROL_REL_TOLERANCE;
  return dist(expectedC1, c.c1) <= tol && dist(expectedC2, c.c2) <= tol;
}

/**
 * Emits the same construction a PDF generator does, counter-clockwise from the
 * positive x axis. Used by tests and by the fixture generator so that what is
 * drawn and what is expected cannot drift apart.
 */
export function circleToBeziers(centre: Point, radius: number): PathSegment[] {
  const k = KAPPA * radius;
  const { x, y } = centre;
  const r = radius;
  return [
    { kind: 'move', to: { x: x + r, y } },
    { kind: 'curve', c1: { x: x + r, y: y + k }, c2: { x: x + k, y: y + r }, to: { x, y: y + r } },
    { kind: 'curve', c1: { x: x - k, y: y + r }, c2: { x: x - r, y: y + k }, to: { x: x - r, y } },
    { kind: 'curve', c1: { x: x - r, y: y - k }, c2: { x: x - k, y: y - r }, to: { x, y: y - r } },
    { kind: 'curve', c1: { x: x + k, y: y - r }, c2: { x: x + r, y: y - k }, to: { x: x + r, y } },
    { kind: 'close' },
  ];
}
